import { defineStore } from 'pinia'
import type { Member } from '@/types/member'
import { getMember as getMemberService, getAuth as getAuthService } from '@/service/member'
import { logout as serviceLogout } from '@/service/login'

import type { AuthStep, PendingThirdPartyAuth } from '@/types/auth'
import { AuthType } from '@/types/member'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<Member | null>(null)
  const isAlreadyGetMember = ref(false)

  const isLogin = computed(() => !!user.value?.account || !!user.value?.email)

  const userNickname = computed(() => {
    if (user.value?.nickname) return user.value.nickname

    /**
     * TODO:
     * 在沒有 nickname 時，需依照不同 auth type 切換 nickname 來源；
     * 需要和後端確認 FB/Google 登入時，是否會有 nickname，若無，前端需自行處理
     */
    switch (user.value?.auth_type) {
      case AuthType.FACEBOOK:
      case AuthType.GOOGLE:
      case AuthType.EMAIL:
      default:
        return user.value?.email.split('@')[0]
    }
  })

  const fbAvatar = computed(() => {
    const fbId = user.value?.account
    const isFbLogin = user.value?.auth_type === AuthType.FACEBOOK
    return fbId && isFbLogin ? `https://graph.facebook.com/v22.0/${fbId}/picture?type=large&width=240&height=240` : ''
  })
  const avatar = computed(() => {
    const DEFAULT_AVATAR = '/images/avatar.png'
    return fbAvatar.value || DEFAULT_AVATAR
  })

  const updateMember = ({ key, value }: { key: keyof Member; value: number | string }) => {
    if (!user.value) return
    user.value = {
      ...user.value,
      [key]: value,
    }
  }

  const getAuth = async () => {
    if (isLogin.value) return
    try {
      const getAuthRes = await getAuthService()
      if (getAuthRes.code !== 0) {
        throw getAuthRes
      }

      return getMember()
    } catch (e) {
      if (import.meta.server) return
      console.error(e)
    }
  }
  const getMember = async () => {
    try {
      const { data } = await getMemberService()
      user.value = data
    } catch (e) {
      if (import.meta.server) return
      console.error(e)
    }
  }

  const logout = async (clear = true) => {
    try {
      await serviceLogout()
      if (clear) {
        user.value = null
      }
    } catch (err) {
      if (err instanceof ErrorEvent) {
        console.error(err.message)
      }
    }
  }

  // Modal
  const isLoginModalShown = ref(false)
  const authStep = ref<AuthStep>('login-menu')
  /** 控制顯示哪個 Modal：main / confirm-discard / third-party-consent */
  const authLayer = ref<'main' | 'confirm-discard' | 'third-party-consent'>('main')

  /** 進入 confirm-discard 前的 layer，取消時用來還原 */
  const previousLayer = ref<'main' | 'third-party-consent' | null>(null)

  /** 暫存 Google/FB token，等用戶同意條款後才呼叫 register API */
  const pendingThirdPartyAuth = ref<PendingThirdPartyAuth | null>(null)

  const openLoginModal = () => {
    isLoginModalShown.value = true
  }

  const closeLoginModal = () => {
    authLayer.value = 'main'
    previousLayer.value = null
    isLoginModalShown.value = false
    authStep.value = 'login-menu'
    pendingThirdPartyAuth.value = null
  }

  const changeAuthStep = (value: AuthStep) => {
    authStep.value = value
  }

  /** true = 已阻止關閉並顯示確認彈窗，false = 允許關閉 */
  const tryCloseLoginModal = (shouldConfirmClose: boolean = false): boolean => {
    if (authStep.value === 'sign-up' && shouldConfirmClose) {
      previousLayer.value = 'main'
      authLayer.value = 'confirm-discard'
      return true
    }
    return false
  }

  const tryCloseNonMainLayer = (): boolean => {
    if (authLayer.value === 'confirm-discard') {
      const returnedLayer = cancelConfirmClose()
      return returnedLayer !== 'main'
    }

    if (authLayer.value === 'third-party-consent') {
      previousLayer.value = 'third-party-consent'
      authLayer.value = 'confirm-discard'
      return true
    }

    return false
  }

  const cancelConfirmClose = () => {
    authLayer.value = previousLayer.value ?? 'main'
    previousLayer.value = null
    return authLayer.value
  }

  const confirmCloseLoginModal = () => {
    isLoginModalShown.value = false
  }

  const setPendingThirdPartyAuth = (auth: PendingThirdPartyAuth) => {
    pendingThirdPartyAuth.value = auth
  }

  return {
    user,
    isLogin,
    isAlreadyGetMember,
    getAuth,
    getMember,
    logout,
    updateMember,
    userNickname,
    fbAvatar,
    avatar,

    isLoginModalShown,
    authStep,
    authLayer,
    previousLayer,
    pendingThirdPartyAuth,
    openLoginModal,
    closeLoginModal,
    changeAuthStep,
    tryCloseLoginModal,
    tryCloseNonMainLayer,
    cancelConfirmClose,
    confirmCloseLoginModal,
    setPendingThirdPartyAuth,
  }
})
